/**
 * EstimateCheckCard — the assistant surface's centrepiece: a contractor's
 * quote read line by line, with each line carrying a verdict (design import,
 * spec §4.4, decision D1).
 *
 * THREE COLUMNS, NOT FOUR. The design draws Line / Contractor / GardEn check
 * / Fair price. The backend has exactly one quantity per line
 * (`MaterialItem.qty`) — the contractor's own as-extracted number; nothing
 * recomputes a corrected quantity. Rendering both a "Contractor" and a
 * "GardEn check" column today would show two cells differing only in
 * formatting, which reads as a bug and undermines the trust the card exists
 * to build. So this ships as Line (+ verdict badge) / Quantity / Fair price,
 * grid `1.9fr 1.1fr 1fr`.
 *
 * `MaterialItem.warning` IS real backend data — it already carries the
 * advisory text the design shows in the (unbuilt) GardEn-check column
 * ("likely 9 — depends on tile size", "ask for an itemized list"). It
 * renders beneath the line name, next to the badge, where the design already
 * stacks secondary information — not as an empty fourth column.
 *
 * Verdict derivation is `lineVerdict` (estimate-verdict.ts): a client
 * inference today, superseded the moment the backend states a `verdict`
 * (capability C2). Colour is never the only carrier of the verdict — the
 * rotated square is `aria-hidden`, and the badge's own words say the state.
 *
 * Contrast floor (spec §3): accent TEXT uses `--gd-accent-ink` (5.80:1);
 * accent SHAPES (the rotated square) keep `--gd-accent` (4.18:1, fine at the
 * 3:1 floor for non-text). Getting this backwards ships unreadable text.
 *
 * Money is the Decimal-as-JSON **string** convention — parsed once with
 * `parseDecimal`, formatted with `fmtSom`. `warning`/`names`/`unit` are
 * `{ru,ky,en}` bags picked with `pickLocalized` (RU fallback, REQ-PRD-§13.2).
 *
 * Never collapses to an empty card (spec §6): a quote where every line is
 * `advice` still renders every row and a footer reading "0 of N".
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  pickLocalized,
  type MaterialItem,
  type SellerMatch,
} from "@/lib/garden-api";
import { fmtSom, parseDecimal } from "@/lib/format";
import type { MaterialsEvent } from "@/features/chat/useChatStream";
import {
  lineVerdict,
  type LineVerdict,
} from "@/features/chat/components/estimate-verdict";

export interface EstimateCheckCardProps {
  materials: MaterialsEvent;
  /** The source document's filename, shown in the header meta line. */
  sourceLabel?: string;
}

/** Narrow the live i18n language to the ru/ky/en `pickLocalized`/`fmtSom` expect. */
function narrowLang(raw: string): "ru" | "ky" | "en" {
  const base = raw.split("-")[0] ?? "ru";
  return base === "ky" || base === "en" ? base : "ru";
}

/** Design table §4.4: square is decoration (`--gd-accent` clears 3:1); text
 * carries the meaning and must clear 4.5:1, hence `--gd-accent-ink` for advice. */
const VERDICT_STYLE: Record<LineVerdict, { square: string; text: string }> = {
  stock: { square: "var(--gd-primary)", text: "var(--gd-primary)" },
  advice: { square: "var(--gd-accent)", text: "var(--gd-accent-ink)" },
  ask_contractor: { square: "var(--gd-neutral)", text: "var(--gd-ink-3)" },
};

/**
 * The cheapest match backs the "fair price" cell.
 *
 * This is REACHABLE, not merely defensive: `quotes/service.py` sets
 * `cheapest = index == 0` over the raw match list but `continue`s past a
 * match whose seller row has since been deleted — so a removed cheapest
 * seller yields an array where *no* entry is flagged `cheapest`. Picking
 * `matches[0]` in that case would only be correct by luck of the
 * repository's price-ascending ordering (`repository.py`), not by
 * construction. So: prefer the flagged match; if none is flagged, compute
 * the true minimum by price instead of trusting array order.
 */
function fairMatch(matches: readonly SellerMatch[]): SellerMatch | null {
  if (matches.length === 0) return null;
  const flagged = matches.find((m) => m.cheapest);
  if (flagged) return flagged;
  return matches.reduce((min, m) =>
    parseDecimal(m.price_kgs) < parseDecimal(min.price_kgs) ? m : min,
  );
}

interface RowProps {
  material: MaterialItem;
  lang: "ru" | "ky" | "en";
}

function EstimateRow({ material, lang }: RowProps): ReactElement {
  const { t } = useTranslation("chat");
  const verdict = lineVerdict({
    matches: material.matches,
    confidence: material.confidence,
  });
  const style = VERDICT_STYLE[verdict];
  const name = pickLocalized(material.names, lang);
  const unit = pickLocalized(material.unit, lang);
  const warning = material.warning ? pickLocalized(material.warning, lang) : "";
  const qtyDisplay = String(parseDecimal(material.qty));
  const match = fairMatch(material.matches);
  // F2: the price is per-unit, not per-line — «620 сом» beside «40 мешок»
  // reads as the line total (620 × 40 ≠ nothing on the card reconciles).
  // Render the unit inline so it can never be misread as the line price.
  const priceText = match
    ? unit
      ? t("estimate.perUnit", { price: fmtSom(parseDecimal(match.price_kgs), lang), unit })
      : fmtSom(parseDecimal(match.price_kgs), lang)
    : t("estimate.noPrice");

  return (
    <div
      data-testid={`estimate-row-${material.id}`}
      data-verdict={verdict}
      className="grid items-start gap-[10px] px-[22px] py-[14px]"
      style={{
        gridTemplateColumns: "1.9fr 1.1fr 1fr",
        borderTop: "1px solid var(--gd-rule-5)",
      }}
    >
      <div className="flex flex-col gap-1 min-w-0">
        <span
          className="text-[15px] font-semibold leading-snug break-words"
          style={{ color: "var(--gd-ink-body)", fontFamily: "var(--gd-text)" }}
        >
          {name}
        </span>
        <span
          className="inline-flex w-fit items-center gap-[5px] text-[11.5px] font-semibold"
          style={{ color: style.text, fontFamily: "var(--gd-text)" }}
        >
          <span
            aria-hidden="true"
            className="inline-block h-[5px] w-[5px] rotate-45"
            style={{ backgroundColor: style.square }}
          />
          {t(`estimate.verdict.${verdict}`)}
        </span>
        {warning ? (
          <span
            className="text-[13.5px] leading-snug"
            style={{ color: "var(--gd-ink-3)", fontFamily: "var(--gd-text)" }}
          >
            {warning}
          </span>
        ) : null}
      </div>

      <div
        className="text-[14.5px]"
        style={{ color: "var(--gd-ink-3)", fontFamily: "var(--gd-text)" }}
      >
        {qtyDisplay} {unit}
      </div>

      <div
        data-testid="estimate-price"
        className="text-[14.5px] font-semibold text-right"
        style={{
          color: match ? "var(--gd-ink-body)" : "var(--gd-ink-3)",
          fontFamily: "var(--gd-text)",
        }}
      >
        {priceText}
      </div>
    </div>
  );
}

export function EstimateCheckCard({
  materials,
  sourceLabel,
}: EstimateCheckCardProps): ReactElement {
  const { t, i18n } = useTranslation("chat");
  const lang = narrowLang(i18n.language);

  const linesMeta = t("estimate.linesMeta", { count: materials.materials.length });
  const metaText = sourceLabel ? `${sourceLabel} · ${linesMeta}` : linesMeta;

  const matched = materials.summary.matched_positions;
  const total = materials.summary.positions;
  // F1 / spec §6: nothing was priced when matched === 0 — the total is
  // UNKNOWN, not zero. A "0 сом" figure on the money card is the one thing
  // it must never show, so the right-hand node is omitted entirely rather
  // than rendering a false zero.
  const hasTotal = matched > 0;
  const fairTotal = fmtSom(parseDecimal(materials.summary.min_total_kgs), lang);

  return (
    <div
      data-testid="estimate-check-card"
      className="overflow-hidden rounded-[18px]"
      style={{
        backgroundColor: "var(--gd-surface)",
        border: "1px solid var(--gd-rule)",
      }}
    >
      <div
        className="px-[22px] py-[16px]"
        style={{ borderBottom: "1px solid var(--gd-rule)" }}
      >
        <div
          className="text-[17px] font-bold"
          style={{ color: "var(--gd-ink)", fontFamily: "var(--gd-display)" }}
        >
          {t("estimate.title")}
        </div>
        <div
          data-testid="estimate-source"
          className="mt-[2px] text-[13.5px]"
          style={{ color: "var(--gd-ink-3)", fontFamily: "var(--gd-text)" }}
        >
          {metaText}
        </div>
      </div>

      {/* Semibold, the design's weight. `--gd-mono` is IBM Plex Mono in
          every language, and its 600 is a real face (design-world.css). */}
      <div
        className="grid gap-[10px] px-[22px] py-[10px] text-[11.5px] font-semibold uppercase"
        style={{
          gridTemplateColumns: "1.9fr 1.1fr 1fr",
          color: "var(--gd-ink-3)",
          fontFamily: "var(--gd-mono)",
          letterSpacing: "0.1em",
        }}
      >
        <span>{t("estimate.colLine")}</span>
        <span>{t("estimate.colQty")}</span>
        <span className="text-right">{t("estimate.colPrice")}</span>
      </div>

      {materials.materials.map((material) => (
        <EstimateRow key={material.id} material={material} lang={lang} />
      ))}

      <div
        data-testid="estimate-footer"
        className="flex flex-wrap items-center justify-between gap-3 px-[22px] py-[14px]"
        style={{ backgroundColor: "var(--gd-wash)" }}
      >
        {/*
         * M8: coverageLead + coverageTrail are two i18n keys forming ONE
         * sentence (count first, description second — "1 из 3" + "позиций
         * с подтверждённым наличием"). They must stay paired: editing one
         * half without the other silently breaks the sentence in every
         * language. See the `_comment` key alongside them in each locale
         * file.
         */}
        <div
          className="text-[14.5px]"
          style={{ color: "var(--gd-ink-2)", fontFamily: "var(--gd-text)" }}
        >
          <strong
            className="font-bold"
            style={{ color: "var(--gd-primary)" }}
          >
            {t("estimate.coverageLead", { matched, total })}
          </strong>{" "}
          {t("estimate.coverageTrail")}
        </div>
        {hasTotal ? (
          <div
            data-testid="estimate-fair-total"
            className="text-[15.5px] font-bold"
            style={{ color: "var(--gd-primary)", fontFamily: "var(--gd-text)" }}
          >
            {t("estimate.fairTotal", { total: fairTotal })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default EstimateCheckCard;
